import axiosInstance from 'api/axiosInstance';
import getUserInfo from 'api/login/getUserInfo';
import {
  ApiResponse,
  BoardWithCreator,
  BoardWithColumns,
  WorkItem,
  User,
  Column,
  Board,
  LinkedBoardInfo,
  WorkItemFilters,
  PaginatedWorkItemsResponse,
  ChecklistItem,
} from './types';

// Helper to get base URL (preserves localhost dev support)
const getTodoBaseUrl = (): string => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:10000/api3.2/todo';
  }
  return '/api3.2/todo';
};

// Helper function to get X-Forwarded-Host header
const getForwardedHost = (): string => {
  const baseUrl = getTodoBaseUrl();

  if (baseUrl.startsWith('/')) {
    return window.location.host;
  }

  try {
    const parsedUrl = new URL(baseUrl);
    return parsedUrl.host || window.location.host;
  } catch (error) {
    return window.location.host;
  }
};

// Check if running on localhost
const isLocalhost = (): boolean => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

// Helper to build request config with proper headers and credentials handling
// Disables withCredentials for localhost to avoid CORS issues with the local Todo backend
const buildRequestConfig = <T extends object>(config: T = {} as T): T & { headers: object; withCredentials?: boolean } => {
  return {
    ...config,
    headers: {
      ...(config as { headers?: object }).headers,
      'X-Forwarded-Host': getForwardedHost(),
    },
    ...(isLocalhost() ? { withCredentials: false } : {}),
  };
};

// Helper function to get user ID from user info
const getUserId = async (): Promise<number> => {
  try {
    const userInfo = await getUserInfo();
    return userInfo.data.UserId;
  } catch (error) {
    console.error('[getUserId] Failed to fetch user info:', error);
    throw new Error('Unable to fetch user information');
  }
};

// Helper function to unwrap API response and handle errors
const unwrapResponse = <T>(
  response: { data: ApiResponse<T> | T }
): T => {
  const apiResponse = response.data as ApiResponse<T>;
  
  if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse) {
    if (!apiResponse.success) {
      const error = new Error(apiResponse.error?.message || 'Unknown error') as Error & { code?: string };
      error.code = apiResponse.error?.code;
      throw error;
    }
    return apiResponse.data!;
  }
  
  // Handle legacy responses that return data directly
  return response.data as T;
};

// Board APIs

export const getBoards = async (): Promise<BoardWithCreator[]> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.get<ApiResponse<BoardWithCreator[]>>(
      `${getTodoBaseUrl()}/boards`,
      buildRequestConfig({ params: { user_id: userId } })
    );
    return unwrapResponse<BoardWithCreator[]>(response);
  } catch (error) {
    console.error('[getBoards] Failed:', error);
    throw error;
  }
};

export const getBoard = async (boardId: number): Promise<BoardWithColumns> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.get<ApiResponse<BoardWithColumns>>(
      `${getTodoBaseUrl()}/boards/${boardId}`,
      buildRequestConfig({ params: { user_id: userId } })
    );
    return unwrapResponse<BoardWithColumns>(response);
  } catch (error) {
    console.error('[getBoard] Failed:', error);
    throw error;
  }
};

export const createBoard = async (
  name: string,
  isPrivate: boolean
): Promise<BoardWithColumns> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.post<ApiResponse<BoardWithColumns>>(
      `${getTodoBaseUrl()}/boards`,
      {
        user_id: userId,
        name,
        is_private: isPrivate,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<BoardWithColumns>(response);
  } catch (error) {
    console.error('[createBoard] Failed:', error);
    throw error;
  }
};

export const updateBoard = async (
  boardId: number,
  name: string,
  isPrivate: boolean
): Promise<Board> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.put<ApiResponse<Board>>(
      `${getTodoBaseUrl()}/boards/${boardId}`,
      {
        user_id: userId,
        name,
        is_private: isPrivate,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<Board>(response);
  } catch (error) {
    console.error('[updateBoard] Failed:', error);
    throw error;
  }
};

export const deleteBoard = async (boardId: number): Promise<void> => {
  try {
    const userId = await getUserId();
    await axiosInstance.delete<ApiResponse<{ message: string }>>(
      `${getTodoBaseUrl()}/boards/${boardId}`,
      buildRequestConfig({ params: { user_id: userId } })
    );
  } catch (error) {
    console.error('[deleteBoard] Failed:', error);
    throw error;
  }
};

// Column APIs

export const createColumn = async (
  boardId: number,
  name: string
): Promise<Column> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.post<ApiResponse<Column>>(
      `${getTodoBaseUrl()}/boards/${boardId}/columns`,
      {
        user_id: userId,
        name,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<Column>(response);
  } catch (error) {
    console.error('[createColumn] Failed:', error);
    throw error;
  }
};

export const updateColumn = async (
  columnId: number,
  name: string
): Promise<Column> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.put<ApiResponse<Column>>(
      `${getTodoBaseUrl()}/columns/${columnId}`,
      {
        user_id: userId,
        name,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<Column>(response);
  } catch (error) {
    console.error('[updateColumn] Failed:', error);
    throw error;
  }
};

export const reorderColumns = async (
  boardId: number,
  columnOrder: number[]
): Promise<void> => {
  try {
    const userId = await getUserId();
    await axiosInstance.put<ApiResponse<{ message: string }>>(
      `${getTodoBaseUrl()}/boards/${boardId}/columns/reorder`,
      {
        user_id: userId,
        column_order: columnOrder,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
  } catch (error) {
    console.error('[reorderColumns] Failed:', error);
    throw error;
  }
};

export const deleteColumn = async (columnId: number): Promise<void> => {
  try {
    const userId = await getUserId();
    await axiosInstance.delete<ApiResponse<{ message: string }>>(
      `${getTodoBaseUrl()}/columns/${columnId}`,
      buildRequestConfig({ params: { user_id: userId } })
    );
  } catch (error) {
    console.error('[deleteColumn] Failed:', error);
    throw error;
  }
};

// Work Item APIs

export const getWorkItems = async (boardId: number, filters?: WorkItemFilters, perColumnLimit?: number): Promise<PaginatedWorkItemsResponse> => {
  try {
    const userId = await getUserId();
    const params: Record<string, string | number> = { user_id: userId };

    if (filters?.assigneeIds && filters.assigneeIds.length > 0) {
      params.assignee_ids = filters.assigneeIds.join(',');
    }
    if (filters?.caseIds && filters.caseIds.length > 0) {
      params.case_ids = filters.caseIds.join(',');
    }
    if (perColumnLimit) {
      params.per_column_limit = perColumnLimit;
    }

    const response = await axiosInstance.get<ApiResponse<PaginatedWorkItemsResponse>>(
      `${getTodoBaseUrl()}/boards/${boardId}/work-items`,
      buildRequestConfig({ params })
    );
    return unwrapResponse<PaginatedWorkItemsResponse>(response);
  } catch (error) {
    console.error('[getWorkItems] Failed:', error);
    throw error;
  }
};

export const createWorkItem = async (
  boardId: number,
  columnId: number,
  title: string,
  description?: string,
  caseId?: number,
  caseTitle?: string,
  dateDue?: string | null,
  checklist?: ChecklistItem[] | null
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.post<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/boards/${boardId}/work-items`,
      {
        user_id: userId,
        column_id: columnId,
        title,
        description: description || null,
        case_id: caseId || null,
        case_title: caseTitle || null,
        date_due: dateDue || null,
        checklist: checklist ? JSON.stringify(checklist) : null,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[createWorkItem] Failed:', error);
    throw error;
  }
};

export const updateWorkItem = async (
  workItemId: number,
  title: string,
  description?: string,
  caseId?: number | null,
  caseTitle?: string | null,
  dateDue?: string | null,
  checklist?: ChecklistItem[] | null
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const body: Record<string, unknown> = {
      user_id: userId,
      title,
      description: description || null,
    };
    if (caseId !== undefined) {
      body.case_id = caseId;
    }
    if (caseTitle !== undefined) {
      body.case_title = caseTitle;
    }
    if (dateDue !== undefined) {
      body.date_due = dateDue;
    }
    if (checklist !== undefined) {
      body.checklist = checklist ? JSON.stringify(checklist) : null;
    }
    const response = await axiosInstance.put<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}`,
      body,
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[updateWorkItem] Failed:', error);
    throw error;
  }
};

export const moveWorkItem = async (
  workItemId: number,
  columnId: number,
  sortOrder: number
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.put<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/move`,
      {
        user_id: userId,
        column_id: columnId,
        sort_order: sortOrder,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[moveWorkItem] Failed:', error);
    throw error;
  }
};

export const deleteWorkItem = async (workItemId: number): Promise<void> => {
  try {
    const userId = await getUserId();
    await axiosInstance.delete<ApiResponse<{ message: string }>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}`,
      buildRequestConfig({ params: { user_id: userId } })
    );
  } catch (error) {
    console.error('[deleteWorkItem] Failed:', error);
    throw error;
  }
};

export const assignUser = async (
  workItemId: number,
  assigneeUserId: number
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.post<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/assignees`,
      {
        user_id: userId,
        assignee_user_id: assigneeUserId,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[assignUser] Failed:', error);
    throw error;
  }
};

export const removeAssignee = async (
  workItemId: number,
  assigneeUserId: number
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.delete<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/assignees/${assigneeUserId}`,
      buildRequestConfig({ params: { user_id: userId } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[removeAssignee] Failed:', error);
    throw error;
  }
};

// Linking APIs

export const importWorkItem = async (
  workItemId: number,
  targetBoardId: number,
  targetColumnId: number
): Promise<WorkItem> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.post<ApiResponse<WorkItem>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/import`,
      {
        user_id: userId,
        target_board_id: targetBoardId,
        target_column_id: targetColumnId,
      },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<WorkItem>(response);
  } catch (error) {
    console.error('[importWorkItem] Failed:', error);
    throw error;
  }
};

export const completeWorkItem = async (
  workItemId: number
): Promise<{ affected_board_ids: number[] }> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.put<ApiResponse<{ affected_board_ids: number[] }>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/complete`,
      { user_id: userId },
      buildRequestConfig({ headers: { 'Content-Type': 'application/json' } })
    );
    return unwrapResponse<{ affected_board_ids: number[] }>(response);
  } catch (error) {
    console.error('[completeWorkItem] Failed:', error);
    throw error;
  }
};

export const getLinkedBoards = async (
  workItemId: number
): Promise<LinkedBoardInfo[]> => {
  try {
    const userId = await getUserId();
    const response = await axiosInstance.get<ApiResponse<LinkedBoardInfo[]>>(
      `${getTodoBaseUrl()}/work-items/${workItemId}/linked-boards`,
      buildRequestConfig({ params: { user_id: userId } })
    );
    return unwrapResponse<LinkedBoardInfo[]>(response);
  } catch (error) {
    console.error('[getLinkedBoards] Failed:', error);
    throw error;
  }
};

// User API (no user_id required per spec)

export const getUsers = async (): Promise<User[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<User[]>>(
      `${getTodoBaseUrl()}/users`,
      buildRequestConfig()
    );
    return unwrapResponse<User[]>(response);
  } catch (error) {
    console.error('[getUsers] Failed:', error);
    throw error;
  }
};
